import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import ErrorPopup from "../views/components/common/pop-up/ErrorPopup";
import SuccessPopup from "../views/components/common/pop-up/SuccessPopup";
import WarningPopup from "../views/components/common/pop-up/WarningPopup";

interface NotificationContextProps {
  errorTitle: string;
  errorMessage: string;
  successMessage: string;
  showError: (title: string, message: string) => void;
  showWarning: (message: string) => void;
  showSuccess: (message: string) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  const showError = useCallback((title: string, message: string) => {
    setErrorTitle(title);
    setErrorMessage(message);
    setOpenError(true);
  }, []);

  const showWarning = useCallback((message: string) => {
    setErrorTitle("Warning");
    setErrorMessage(message);
    setOpenWarning(true);
  }, []);

  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setOpenSuccess(true);
  }, []);

  const hideNotification = useCallback(() => {
    setErrorTitle("");
    setErrorMessage("");
    setSuccessMessage("");
    setOpenError(false);
    setOpenSuccess(false);
    setOpenWarning(false);
  }, []);

  const value = useMemo(
    () => ({
      errorTitle,
      errorMessage,
      successMessage,
      showWarning,
      showError,
      showSuccess,
      hideNotification,
    }),
    [
      errorTitle,
      errorMessage,
      successMessage,
      showWarning,
      showError,
      showSuccess,
      hideNotification,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <WarningPopup
        open={openWarning}
        warningTitle={errorTitle}
        warningMessage={errorMessage}
        onClose={hideNotification}
      />
      <ErrorPopup
        open={openError}
        errorTitle={errorTitle}
        errorMessage={errorMessage}
        onClose={hideNotification}
      />
      <SuccessPopup
        open={openSuccess}
        successMessage={successMessage}
        onClose={hideNotification}
      />
    </NotificationContext.Provider>
  );
};
